<template>
  <NuxtLayout name="default">
    <template #header>
      <!--
        Backend healthy (4xx — e.g. 404/403): render the full site header.
        Its menu was already fetched during the original navigation and is
        served from cache, so no risky refetch happens here.
      -->
      <SiteHeader v-if="!isServerError" />

      <!--
        Backend unhealthy (5xx / unreachable): the module reports 500/503 and
        there is no cached menu, so <SiteHeader> would try (and fail) to fetch
        the menu while rendering this very page. Use a static, fetch-free header
        so the error page always renders.
      -->
      <header v-else class="relative z-110 background-pattern-navy-500--hero">
        <ContentContainer>
          <nav class="py-5 flex items-center">
            <NuxtLink to="/" aria-label="Back to home page" class="inline-flex">
              <MBZUAILogo :is-home-page="true" />
            </NuxtLink>
          </nav>
        </ContentContainer>
      </header>
    </template>

    <template #content>
      <Error404 v-if="error?.statusCode === 404" />

      <!--
        `v-else-if="error"` (not bare `v-else`): when navigating away, Nuxt
        clears the error to null mid-transition. A bare v-else would then render
        this block with `error?.statusCode || 500` → a brief bogus "500" flash
        before the destination page mounts. Guarding on `error` avoids that.
      -->
      <section
        v-else-if="error"
        class="w-full py-[42px] lg:py-21 md:py-32 flex flex-col items-center justify-center text-center min-h-[60vh] bg-white"
      >
        <ContentContainer class="flex flex-col items-center gap-6">
          <h1 class="text-[120px] font-bold leading-none text-mbzuai-navy-500">
            {{ error?.statusCode || 500 }}
          </h1>
          <h3 class="text-h3 font-bold text-mbzuai-navy-500">
            {{ errorTitle }}
          </h3>
          <p class="text-lg text-mbzuai-sand-800 max-w-md flex flex-col gap-2">
            {{ errorMessage }}
          </p>

          <!-- Error details -->
          <div
            v-if="
              error && (currentUser?.roles?.includes('administrator') || isDev)
            "
            class="p-4 bg-mbzuai-red-900 text-left max-w-4xl w-full overflow-auto flex flex-col gap-4"
          >
            <p
              class="text-surface-50 font-mono whitespace-pre-wrap wrap-break-word"
            >
              {{ error?.message }}
            </p>

            <details
              v-if="error?.stack"
              class="text-surface-100 whitespace-pre-wrap wrap-break-word text-left text-sm"
            >
              <summary class="cursor-pointer">Stack trace</summary>
              <pre class="mt-4">{{ error.stack }}</pre>
            </details>
          </div>

          <ParagraphCallToActionDefault
            button-style="primary"
            button-label="Back to Home"
            :button-url="{
              title: 'Back to home page',
              href: '/',
            }"
            class="mt-4"
          />
        </ContentContainer>
      </section>
    </template>
  </NuxtLayout>
</template>

<script setup lang="ts">
const error = useError();
const { user: currentUser } = useCurrentUser();

// Show the debug details block only in dev (or to admins). `import.meta.dev`
// is compiled to a constant, so the block is tree-shaken out of production.
const isDev = import.meta.dev;
// Any 5xx code is assumed as "backend is not reachable".
// If so, the menu can't be fetched so we show a static header instead
// 4xx errors (404/403/…) can show the full header
const isServerError = computed(() => {
  const code = Number(error.value?.statusCode);
  return !code || code >= 500;
});

const errorTitle = computed(() => {
  if (error.value?.statusCode === 404) return 'Page Not Found';
  if (error.value?.statusCode === 403) return 'Access Denied';
  return 'Something Went Wrong';
});

const errorMessage = computed(() => {
  if (error.value?.statusCode === 404) {
    return 'The page you are looking for does not exist or has been moved.';
  }
  if (error.value?.statusCode === 403) {
    return 'You do not have permission to access this page.';
  }
  return 'We encountered an unexpected error. Please try again later.';
});

useHead({
  title: `${error.value?.statusCode || 'Error'} - ${errorTitle.value}`,
  meta: [{ name: 'robots', content: 'noindex, nofollow' }],
});
</script>
